// components/Theme.jsx
import { ThemeProvider, createGlobalStyle } from "styled-components";
import { useThemeContext } from "../context/Theme";
import React from "react";

// Add global styles to fix mobile issues
const GlobalStyle = createGlobalStyle`
  /* Mobile styles — aligned with MUI sm breakpoint (600px) */
  @media (max-width: 600px) {
    /* Prevent unwanted scroll */
    html.noscroll,
    body.noscroll {
      overflow: hidden !important;
      width: 100% !important;
      height: 100% !important;
    }
    
    /* Animation in progress styles */
    body.chat-animating {
      overflow: hidden !important;
      pointer-events: none;
    }
    
    body.chat-animating .toggle-chat-container {
      pointer-events: none !important;
    }
    
    body.chat-animating .chat-button {
      opacity: 0.5;
      transition: opacity 0.3s ease;
    }
    
    /* Hide the chat container during the close animation to prevent flicker */
    body.chat-animating .mia-ChatAndSearchContainer {
      visibility: hidden !important;
      opacity: 0 !important;
      pointer-events: none !important;
    }
    
    /* When closing, make sure the exit is clean by using GPU acceleration */
    .mia-ChatAndSearchContainer {
      -webkit-transform: translate3d(0, 0, 0);
      transform: translate3d(0, 0, 0);
      backface-visibility: hidden;
      perspective: 1000px;
      will-change: transform, opacity;
    }
    
    body.car-search-open {
      overflow: hidden !important;
      width: 100%;
      height: 100%;
      touch-action: none; /* Prevent iOS Safari bounce effect */
    }
    
    /* Transparency effect for chat on mobile */
    .mia-ChatAndSearchContainer.page-scrolling {
      opacity: 1 !important;
      transition: none !important;
    }
    
    /* Handle active personal data form */
    body.personal-data-form-active {
      overflow: hidden !important;
    }
    
    /* Fix animation flickering issues */
    .chat-body {
      will-change: transform, opacity;
      transform: translateZ(0);
      -webkit-transform: translateZ(0);
      -webkit-backface-visibility: hidden;
      backface-visibility: hidden;
    }
    
    /* Prevent layout shifts when chat is animating */
    .toggle-chat-container {
      isolation: isolate;
      contain: layout size;
    }
    
    /* When chat is in closing state, make sure we don't show any remnants */
    .toggle-chat-container.chat-closing .chat-body {
      display: none !important;
      opacity: 0 !important;
      visibility: hidden !important;
      pointer-events: none !important;
      position: absolute !important;
      left: -9999px !important;
    }
    
    /* Properly handle transitions */
    .mia-ChatAndSearchContainer {
      will-change: transform, opacity;
      transform: translateZ(0);
      transition: opacity 0.3s ease, transform 0.3s ease !important;
    }
    
    /* Handle keyboard opening on personal data form */
    @media (max-height: 500px) {
      .personal-data-form-container,
      .personal-data-form {
        max-height: 100vh !important;
        height: 100vh !important;
        border-radius: 0 !important;
      }
      
      .personal-data-form-content {
        max-height: calc(100vh - 120px) !important;
      }
    }
    
    /* Personal Data Form mobile fixes for reduced mode */
    .personal-data-form-container {
      position: relative;
      z-index: 100;
      background: white;
      margin: 0 !important;
      max-width: 100vw !important;
      width: 100% !important;
      border-radius: 10px 10px 0 0 !important;
      box-shadow: 0 -4px 10px rgba(0, 0, 0, 0.1) !important;
      overflow: hidden !important;
      
      &.compact-mode {
        position: fixed !important;
        bottom: 0 !important;
        left: 0 !important;
        right: 0 !important;
        z-index: 1005 !important;
        max-height: 70vh !important;
        overflow-y: auto !important;
        background-color: white !important;
        border-radius: 12px 12px 0 0 !important;
        box-shadow: 0 -5px 15px rgba(0, 0, 0, 0.1) !important;
      }
    }
    
    .mia-ChatAndSearchContainer.page-scrolling .personal-data-form-container {
      opacity: 1 !important;
      background: white !important;
    }
    
    /* Compact form styles */
    .personal-data-form-container.compact-mode {
      /* Form header in reduced mode */
      .personal-data-form-header {
        padding: 12px !important;
        font-size: 14px !important;
        position: sticky !important;
        top: 0 !important;
        background: white !important;
        z-index: 10 !important;
        border-bottom: 1px solid #eee !important;
      }
      
      /* Form title in reduced mode */
      .personal-data-form-title,
      .MuiTypography-h6 {
        font-size: 16px !important;
        margin-bottom: 4px !important;
      }
      
      /* Form description in reduced mode */
      .personal-data-form-description,
      .MuiTypography-body2 {
        font-size: 12px !important;
      }
      
      /* Form content in reduced mode */
      .personal-data-form-content {
        padding: 12px !important;
        max-height: 200px !important;
        overflow-y: auto !important;
      }
      
      /* Form actions in reduced mode */
      .personal-data-form-actions {
        position: sticky !important;
        bottom: 0 !important;
        background: white !important;
        padding: 12px !important;
        border-top: 1px solid #eee !important;
        display: flex !important;
        justify-content: flex-end !important;
        box-shadow: 0 -2px 5px rgba(0, 0, 0, 0.05) !important;
      }
      
      /* Input fields in reduced mode */
      .MuiTextField-root {
        margin-bottom: 12px !important;
      }
      
      /* Buttons in reduced mode */
      .MuiButton-root {
        padding: 8px 12px !important;
        font-size: 13px !important;
      }
    }

    /* iOS Safari specific fixes */
    @supports (-webkit-touch-callout: none) {
      html.ios-safari {
        height: -webkit-fill-available !important;
      }
      
      body.ios-safari {
        height: -webkit-fill-available !important;
        width: 100% !important;
      }
      
      body.car-search-open.ios-safari {
        min-height: -webkit-fill-available !important;
        overflow: hidden !important;
      }
      
      /* Full height overlay that really works on iOS */
      .car-search-overlay {
        height: 100vh !important; /* Fallback */
        height: -webkit-fill-available !important;
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        right: 0 !important;
        bottom: 0 !important;
        width: 100vw !important;
        margin: 0 !important;
        padding: 0 !important;
        z-index: 9999 !important;
        overflow: hidden !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
      }
      
      /* Container that properly fills screen on iOS */
      .car-search-container {
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        right: 0 !important;
        bottom: 0 !important;
        width: 100% !important;
        max-width: 100% !important;
        height: 100% !important; /* Fallback */
        height: -webkit-fill-available !important;
        max-height: -webkit-fill-available !important;
        margin: 0 !important;
        border-radius: 0 !important;
        box-sizing: border-box !important;
        z-index: 10000 !important;
        display: flex !important;
        flex-direction: column !important;
        overflow: hidden !important;
      }
      
      /* Form that scrolls properly */
      .car-search-form {
        flex: 1 !important;
        display: flex !important;
        flex-direction: column !important;
        max-height: none !important;
        overflow-y: auto !important;
        -webkit-overflow-scrolling: touch !important;
        margin: 0 !important;
        z-index: 10 !important;
      }
      
      /* Content that doesn't get cut off */
      .car-search-content {
        flex: 1 !important;
        overflow-y: auto !important;
        -webkit-overflow-scrolling: touch !important;
        padding-bottom: 90px !important; /* Space for buttons */
      }
      
      /* Fixed buttons that stay visible */
      .car-search-actions {
        position: fixed !important;
        bottom: 0 !important;
        left: 0 !important;
        right: 0 !important;
        z-index: 2002 !important;
        padding: 16px !important;
        padding-bottom: calc(16px + env(safe-area-inset-bottom, 0)) !important; /* iPhone X+ support */
        background-color: white !important;
        box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.15) !important;
      }
      
      /* When keyboard is open, handle special positioning */
      .car-search-container.keyboard-open .car-search-content {
        padding-bottom: 160px !important; /* Extra padding for keyboard case */
      }
      
      /* Special handling for buttons with keyboard open */
      .car-search-container.keyboard-open .car-search-actions {
        position: sticky !important;
        bottom: 0 !important;
        z-index: 2002 !important;
      }
    }
  }
`;

export const StyledComponentsThemWrapper = ({ children }) => {
  const { theme } = useThemeContext();
  
  // Add class to html element for better iOS Safari detection
  React.useEffect(() => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent.toLowerCase());
    
    if (isIOS && isSafari) {
      document.documentElement.classList.add('ios-safari');
      document.body.classList.add('ios-safari');
      
      return () => {
        document.documentElement.classList.remove('ios-safari');
        document.body.classList.remove('ios-safari');
      };
    }
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      {children}
    </ThemeProvider>
  );
};
